import i18n from './i18n'
import { formConfig } from './formData'

// Resolve a titleKey from the survey namespace
function resolve(titleKey) {
  return titleKey ? i18n.t(titleKey, { ns: 'survey' }) : ''
}

// Resolve a responseScaleKey from the survey namespace
function resolveScale(scaleKey) {
  return scaleKey ? i18n.t(scaleKey, { ns: 'survey', returnObjects: true }) : []
}

// Build lookup maps (lazy, refreshed on language change)
let profileMap = null
let matrixMap = null
let groupMap = null
let cachedLng = null

function ensureMaps() {
  const lng = i18n.language
  if (profileMap && cachedLng === lng) return
  cachedLng = lng

  profileMap = new Map()
  formConfig.profileQuestions.forEach(q => {
    profileMap.set(q.id, resolve(q.titleKey))
  })

  matrixMap = new Map()
  groupMap = new Map()
  formConfig.matrixGroups.forEach(group => {
    groupMap.set(group.id, {
      title: resolve(group.titleKey),
      section: group.section,
      responseScale: resolveScale(group.responseScaleKey),
    })
    group.questions.forEach(q => {
      matrixMap.set(q.id, resolve(q.titleKey))
    })
  })
}

/**
 * Get the human-readable label for a question ID.
 * Works for both profile (q1-q13) and likert (dci1_1, pi3_12) questions.
 */
export function getQuestionLabel(questionId) {
  ensureMaps()
  return profileMap.get(questionId) || matrixMap.get(questionId) || questionId
}

/**
 * Get group metadata (title, section, responseScale) by group ID.
 */
export function getGroupInfo(groupId) {
  ensureMaps()
  return groupMap.get(groupId) || null
}

/**
 * Get the scale label for a numeric score (1-5) within a group.
 * Returns e.g. "Often" for score 4 in a DCI group.
 */
export function getScaleLabel(groupId, score) {
  ensureMaps()
  const group = groupMap.get(groupId)
  if (!group || !score) return ''
  return group.responseScale[score - 1] || ''
}

/**
 * Get all matrix groups as an ordered array with resolved titles.
 */
export function getMatrixGroups() {
  return formConfig.matrixGroups.map(group => ({
    ...group,
    title: resolve(group.titleKey),
    questions: group.questions.map(q => ({
      ...q,
      title: resolve(q.titleKey),
    })),
  }))
}

/**
 * Get all profile questions with resolved titles and options.
 */
export function getProfileQuestions() {
  return formConfig.profileQuestions.map(q => ({
    ...q,
    title: resolve(q.titleKey),
    options: q.optionsKey ? i18n.t(q.optionsKey, { ns: 'survey', returnObjects: true }) : undefined,
  }))
}
